import type { BookingLocale } from "@/i18n/types";

export type BookingLegalDocument = "terms" | "privacy";

export type BookingLegalContext = {
  operatorName: string;
  legalRepresentative?: string | null;
  addressLines: string[];
  contactEmail?: string | null;
  contactPhone?: string | null;
  privacyEmail?: string | null;
  supervisoryAuthority?: string | null;
  imprintUrl?: string | null;
  retentionNote?: string | null;
  thirdPartyNote?: string | null;
};

type LegalSection = {
  title: string;
  paragraphs: string[];
};

type LegalDocument = {
  title: string;
  intro?: string;
  sections: LegalSection[];
};

function formatAddress(addressLines: string[]): string {
  return addressLines.filter(Boolean).join(", ");
}

function cancellationContact(locale: BookingLocale, context: BookingLegalContext): string {
  const parts: string[] = [];
  if (context.contactPhone) {
    parts.push(
      locale === "en"
        ? `by phone on ${context.contactPhone}`
        : `telefonisch unter ${context.contactPhone}`,
    );
  }
  if (context.contactEmail) {
    parts.push(
      locale === "en"
        ? `by email to ${context.contactEmail}`
        : `per E-Mail an ${context.contactEmail}`,
    );
  }
  if (!parts.length) {
    return locale === "en" ? "by another verifiable means" : "auf nachweisbarem Weg";
  }
  return locale === "en" ? parts.join(" or ") : parts.join(" oder ");
}

function privacyContactLines(locale: BookingLocale, context: BookingLegalContext): string[] {
  const lines: string[] = [];
  if (context.privacyEmail) {
    lines.push(
      locale === "en"
        ? `Privacy contact: ${context.privacyEmail}`
        : `Kontakt für Datenschutzanfragen: ${context.privacyEmail}`,
    );
  }
  if (context.contactPhone) {
    lines.push(
      locale === "en"
        ? `Phone: ${context.contactPhone}`
        : `Telefon: ${context.contactPhone}`,
    );
  }
  if (context.imprintUrl) {
    lines.push(
      locale === "en"
        ? `Imprint: ${context.imprintUrl}`
        : `Impressum: ${context.imprintUrl}`,
    );
  }
  return lines;
}

function buildTermsDocument(locale: BookingLocale, context: BookingLegalContext): LegalDocument {
  const operator = context.operatorName;
  const address = formatAddress(context.addressLines);
  const cancellation = cancellationContact(locale, context);

  if (locale === "en") {
    return {
      title: "Cancellation terms for online booking requests",
      intro: `These cancellation terms apply to non-binding booking requests submitted via the online booking process of ${operator}.`,
      sections: [
        {
          title: "1. Non-binding request",
          paragraphs: [
            `Your online booking request to ${operator} is non-binding until we expressly confirm it. A contract for accommodation is only formed once we confirm your request in writing (e.g. by email).`,
          ],
        },
        {
          title: "2. Free cancellation",
          paragraphs: [
            `You may cancel your reservation with ${operator} at any time without any processing fee. Please notify us of a cancellation as early as possible – ${cancellation}.`,
          ],
        },
        {
          title: "3. No-show without cancellation",
          paragraphs: [
            "If you do not arrive on the agreed arrival date and have not cancelled your reservation in good time, we are entitled to treat the reservation as void.",
            "In such cases, we reserve the right not to confirm future reservation requests.",
          ],
        },
        {
          title: "4. Accuracy of information",
          paragraphs: [
            "You are obliged to provide all booking details truthfully and completely (in particular party size, vehicle details, arrival type and contact details).",
            "If we subsequently find that material information is incorrect or incomplete, the reservation becomes void with immediate effect. In that case, no right to a pitch exists.",
          ],
        },
        {
          title: "5. Pitch allocation",
          paragraphs: [
            "Even after a reservation has been confirmed, we reserve the right to assign, change or allocate an equivalent pitch number up until arrival.",
            "A confirmed reservation does not entitle you to a specific pitch.",
          ],
        },
        {
          title: "6. Final provisions",
          paragraphs: [
            "German law applies. Should individual provisions be or become invalid, the validity of the remaining provisions shall remain unaffected.",
            address ? `Contractual partner: ${operator}, ${address}.` : `Contractual partner: ${operator}.`,
          ],
        },
      ],
    };
  }

  return {
    title: "Stornobedingungen für Online-Buchungsanfragen",
    intro: `Diese Stornobedingungen gelten für unverbindliche Buchungsanfragen über die Online-Buchungsstrecke von ${operator}.`,
    sections: [
      {
        title: "1. Unverbindliche Anfrage",
        paragraphs: [
          `Ihre Online-Buchungsanfrage bei ${operator} ist bis zur ausdrücklichen Bestätigung durch uns unverbindlich. Ein Beherbergungsvertrag kommt erst zustande, wenn wir Ihre Anfrage schriftlich bestätigen (z. B. per E-Mail).`,
        ],
      },
      {
        title: "2. Kostenfreie Stornierung",
        paragraphs: [
          `Sie können Ihre Reservierung bei ${operator} jederzeit und ohne Bearbeitungsgebühr stornieren. Bitte teilen Sie uns eine Stornierung möglichst frühzeitig mit – ${cancellation}.`,
        ],
      },
      {
        title: "3. Nichterscheinen ohne Stornierung",
        paragraphs: [
          "Erscheinen Sie zum vereinbarten Anreisetag nicht und haben Sie die Reservierung nicht rechtzeitig storniert, sind wir berechtigt, die Reservierung als erloschen zu behandeln.",
          "Wir behalten uns in diesem Fall vor, künftige Reservierungsanfragen nicht mehr zu bestätigen.",
        ],
      },
      {
        title: "4. Richtigkeit der Angaben",
        paragraphs: [
          "Sie sind verpflichtet, alle Buchungsdaten wahrheitsgemäß und vollständig anzugeben (insbesondere Personenzahl, Fahrzeugdaten, Anreiseart und Kontaktdaten).",
          "Stellen wir nachträglich fest, dass wesentliche Angaben unrichtig oder unvollständig sind, erlischt die Reservierung mit sofortiger Wirkung. Ein Anspruch auf einen Stellplatz besteht in diesem Fall nicht.",
        ],
      },
      {
        title: "5. Platzvergabe",
        paragraphs: [
          "Auch nach Bestätigung der Reservierung behalten wir uns das Recht vor, die konkrete Platznummer bis zur Anreise festzulegen, zu ändern oder einen gleichwertigen Stellplatz zuzuweisen.",
          "Eine bestätigte Reservierung begründet keinen Anspruch auf einen bestimmten Stellplatz.",
        ],
      },
      {
        title: "6. Schlussbestimmungen",
        paragraphs: [
          "Es gilt das Recht der Bundesrepublik Deutschland. Sollten einzelne Bestimmungen unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.",
          address ? `Vertragspartner: ${operator}, ${address}.` : `Vertragspartner: ${operator}.`,
        ],
      },
    ],
  };
}

function buildPrivacyDocument(locale: BookingLocale, context: BookingLegalContext): LegalDocument {
  const operator = context.operatorName;
  const address = formatAddress(context.addressLines);
  const responsibleParagraphs: string[] = [];

  if (locale === "en") {
    responsibleParagraphs.push(
      address
        ? `${operator} (${address}) is responsible for processing the personal data provided during online booking (data controller within the meaning of the GDPR).`
        : `${operator} is responsible for processing the personal data provided during online booking (data controller within the meaning of the GDPR).`,
    );
    if (context.legalRepresentative) {
      responsibleParagraphs.push(`Legal representative: ${context.legalRepresentative}.`);
    }
    responsibleParagraphs.push(...privacyContactLines(locale, context));

    const retentionParagraphs = [
      "Personal data is stored only for as long as necessary to process the booking, fulfil the contract, communicate with you and comply with statutory retention obligations.",
    ];
    if (context.retentionNote) retentionParagraphs.push(context.retentionNote);

    const thirdPartyParagraphs = [
      "Data is disclosed to third parties only where necessary to fulfil the contract (e.g. technical service providers, payment processing) or where required by law.",
    ];
    if (context.thirdPartyNote) thirdPartyParagraphs.push(context.thirdPartyNote);

    const rightsParagraphs = [
      "You have the right to access, rectification, erasure, restriction of processing, data portability and to object to processing of your personal data.",
      context.privacyEmail
        ? `To exercise your rights, please contact us at ${context.privacyEmail}.`
        : "To exercise your rights, please contact the data controller using the contact details above.",
    ];
    if (context.supervisoryAuthority) {
      rightsParagraphs.push(
        `You also have the right to lodge a complaint with the following supervisory authority: ${context.supervisoryAuthority}.`,
      );
    } else {
      rightsParagraphs.push(
        "You also have the right to lodge a complaint with a data protection supervisory authority.",
      );
    }

    return {
      title: "Privacy notice for online booking",
      intro: `Protecting your personal data is important to us. Below we explain how ${operator} processes your data when you submit an online booking request.`,
      sections: [
        { title: "1. Data controller", paragraphs: responsibleParagraphs },
        {
          title: "2. Purposes and legal bases",
          paragraphs: [
            "We process your data to handle your booking request, communicate with you and take pre-contractual steps (Art. 6(1)(b) GDPR).",
            "If you use a guest account or give corresponding consent, processing is additionally based on your consent (Art. 6(1)(a) GDPR), which you may withdraw at any time.",
          ],
        },
        {
          title: "3. Data processed",
          paragraphs: [
            "Depending on the booking form, this may include name, address, contact details, travel dates, vehicle and party details, licence plate and optional messages.",
          ],
        },
        { title: "4. Retention period", paragraphs: retentionParagraphs },
        { title: "5. Disclosure to third parties", paragraphs: thirdPartyParagraphs },
        { title: "6. Your rights", paragraphs: rightsParagraphs },
      ],
    };
  }

  responsibleParagraphs.push(
    address
      ? `Verantwortlich für die Verarbeitung der im Rahmen der Online-Buchung angegebenen personenbezogenen Daten ist ${operator}, ${address} (Verantwortlicher im Sinne der Datenschutz-Grundverordnung, DSGVO).`
      : `Verantwortlich für die Verarbeitung der im Rahmen der Online-Buchung angegebenen personenbezogenen Daten ist ${operator} (Verantwortlicher im Sinne der Datenschutz-Grundverordnung, DSGVO).`,
  );
  if (context.legalRepresentative) {
    responsibleParagraphs.push(`Vertretungsberechtigt: ${context.legalRepresentative}.`);
  }
  responsibleParagraphs.push(...privacyContactLines(locale, context));

  const retentionParagraphs = [
    "Personenbezogene Daten werden nur so lange gespeichert, wie dies für die Bearbeitung der Buchung, die Vertragserfüllung, die Kommunikation mit Ihnen sowie die Erfüllung gesetzlicher Aufbewahrungspflichten erforderlich ist.",
  ];
  if (context.retentionNote) retentionParagraphs.push(context.retentionNote);

  const thirdPartyParagraphs = [
    "Eine Übermittlung an Dritte erfolgt nur, soweit dies zur Vertragserfüllung notwendig ist (z. B. technische Dienstleister, Zahlungsabwicklung) oder eine gesetzliche Verpflichtung besteht.",
  ];
  if (context.thirdPartyNote) thirdPartyParagraphs.push(context.thirdPartyNote);

  const rightsParagraphs = [
    "Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie Widerspruch gegen die Verarbeitung Ihrer personenbezogenen Daten.",
    context.privacyEmail
      ? `Zur Ausübung Ihrer Rechte wenden Sie sich bitte an ${context.privacyEmail}.`
      : "Zur Ausübung Ihrer Rechte wenden Sie sich bitte an den Verantwortlichen unter den oben genannten Kontaktdaten.",
  ];
  if (context.supervisoryAuthority) {
    rightsParagraphs.push(
      `Sie haben zudem das Recht, Beschwerde bei folgender Aufsichtsbehörde einzureichen: ${context.supervisoryAuthority}.`,
    );
  } else {
    rightsParagraphs.push(
      "Sie haben zudem das Recht, Beschwerde bei einer Datenschutz-Aufsichtsbehörde einzureichen.",
    );
  }

  return {
    title: "Datenschutzhinweise zur Online-Buchung",
    intro: `Der Schutz Ihrer personenbezogenen Daten ist uns wichtig. Nachfolgend informieren wir Sie, wie ${operator} Ihre Daten im Rahmen der Online-Buchungsanfrage verarbeitet.`,
    sections: [
      { title: "1. Verantwortlicher", paragraphs: responsibleParagraphs },
      {
        title: "2. Zwecke und Rechtsgrundlagen",
        paragraphs: [
          "Wir verarbeiten Ihre Daten zur Bearbeitung Ihrer Buchungsanfrage, zur Kommunikation mit Ihnen sowie zur Durchführung vorvertraglicher Maßnahmen (Art. 6 Abs. 1 lit. b DSGVO).",
          "Sofern Sie ein Gastkonto nutzen oder eine entsprechende Einwilligung erteilen, erfolgt die Verarbeitung zusätzlich auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO), die Sie jederzeit widerrufen können.",
        ],
      },
      {
        title: "3. Verarbeitete Daten",
        paragraphs: [
          "Je nach Buchungsformular können insbesondere Name, Anschrift, Kontaktdaten, Reisedaten, Fahrzeug- und Personenangaben, Kennzeichen sowie optionale Nachrichten verarbeitet werden.",
        ],
      },
      { title: "4. Speicherdauer", paragraphs: retentionParagraphs },
      { title: "5. Weitergabe an Dritte", paragraphs: thirdPartyParagraphs },
      { title: "6. Ihre Rechte", paragraphs: rightsParagraphs },
    ],
  };
}

function withoutBookingWord(text: string, locale: BookingLocale): string {
  if (locale === "en") {
    return text
      .replaceAll("booking requests", "requests")
      .replaceAll("booking request", "request")
      .replaceAll("Booking requests", "Requests")
      .replaceAll("Booking request", "Request")
      .replaceAll("online booking", "online request")
      .replaceAll("Online booking", "Online request")
      .replaceAll("booking details", "details")
      .replaceAll("booking form", "request form")
      .replaceAll("booking", "request")
      .replaceAll("Booking", "Request");
  }
  return text
    .replaceAll("Online-Buchungsstrecke", "Online-Strecke")
    .replaceAll("Online-Buchungsanfragen", "Online-Anfragen")
    .replaceAll("Online-Buchungsanfrage", "Online-Anfrage")
    .replaceAll("Buchungsanfragen", "Anfragen")
    .replaceAll("Buchungsanfrage", "Anfrage")
    .replaceAll("Online-Buchungen", "Online-Anfragen")
    .replaceAll("Online-Buchung", "Online-Anfrage")
    .replaceAll("Buchungsdaten", "Angaben")
    .replaceAll("Buchungsformular", "Anfrageformular")
    .replaceAll("Buchungsbedingungen", "Bedingungen")
    .replaceAll("Buchung", "Anfrage");
}

function applyInquiryLegalWording(document: LegalDocument, locale: BookingLocale): LegalDocument {
  return {
    title: withoutBookingWord(document.title, locale),
    intro: document.intro ? withoutBookingWord(document.intro, locale) : undefined,
    sections: document.sections.map((section) => ({
      title: withoutBookingWord(section.title, locale),
      paragraphs: section.paragraphs.map((paragraph) => withoutBookingWord(paragraph, locale)),
    })),
  };
}

export function getBookingLegalDocument(
  locale: BookingLocale,
  document: BookingLegalDocument,
  context: BookingLegalContext,
  options?: { inquiry?: boolean },
): LegalDocument {
  const content =
    document === "terms"
      ? buildTermsDocument(locale, context)
      : buildPrivacyDocument(locale, context);
  return options?.inquiry ? applyInquiryLegalWording(content, locale) : content;
}

export function bookingLegalContextFromConfig(
  legal:
    | {
        operator_name: string;
        legal_representative?: string | null;
        address_lines?: string[];
        contact_email?: string | null;
        contact_phone?: string | null;
        privacy_email?: string | null;
        supervisory_authority?: string | null;
        imprint_url?: string | null;
        retention_note?: string | null;
        third_party_note?: string | null;
      }
    | null
    | undefined,
  fallbackSiteName: string,
): BookingLegalContext {
  return {
    operatorName: legal?.operator_name?.trim() || fallbackSiteName,
    legalRepresentative: legal?.legal_representative,
    addressLines: legal?.address_lines ?? [],
    contactEmail: legal?.contact_email,
    contactPhone: legal?.contact_phone,
    privacyEmail: legal?.privacy_email,
    supervisoryAuthority: legal?.supervisory_authority,
    imprintUrl: legal?.imprint_url,
    retentionNote: legal?.retention_note,
    thirdPartyNote: legal?.third_party_note,
  };
}
